import { Children, useEffect, useMemo, useRef, useState } from 'react';
import { func, node } from 'prop-types';
import { noop } from 'lodash';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';

import Alerts from '../../alerts/Alerts';
import Panel from '../panel/Panel';
import Layout from './Layout';
import { kabobToPascalCase } from '../../../utilities/StringUtilities';
import useSize from '../../../utilities/UseSize';
import { usePageId } from '../page/PageUtils';

const useStyles = makeStyles(() => ({
    panelScrollContainer: {
        // Prevent horizontal scrollbar when dragging panels.
        overflowX: 'hidden',
    },
}));

export const LAYOUT_WIDTH = {
    FULL: 12,
    HALF: 6,
    THIRD: 4,
    QUARTER: 3,
};

function wrapPanel(panel, panelId) {
    return (
        <Panel key={panel.key} id={panelId} {...panel.props}>
            {panel}
        </Panel>
    );
}

export default function PanelContainer({ children, panelProvider, ...layoutProps }) {
    const styles = useStyles();
    const pageId = usePageId();
    const pageIdPrefix = useMemo(() => kabobToPascalCase(pageId), [pageId]);
    const [panelTypeCounts, setPanelTypeCounts] = useState({});
    const scrollContainerRef = useRef();
    const scrollContainerSize = useSize(scrollContainerRef);
    const [panels, setPanels] = useState([]);

    const createPanel = useRef();
    createPanel.current = (panelTypeName, panelProps) => {
        const panel = panelProvider(panelTypeName, panelProps);
        const hasCount = panelTypeCounts[panelTypeName] !== undefined;
        const newPanelTypeCounts = {
            ...panelTypeCounts,
            [panelTypeName]: hasCount ? panelTypeCounts[panelTypeName] + 1 : 0,
        };
        const panelId = `${pageIdPrefix}-${panelTypeName}${
            newPanelTypeCounts[panelTypeName] > 0 ? newPanelTypeCounts[panelTypeName] : ''
        }`;

        setPanelTypeCounts(newPanelTypeCounts);

        return wrapPanel(panel, panelId);
    };

    const updatePanels = useRef();
    updatePanels.current = (newChildren) => {
        // The default panel ID is just the panel type name as the panel id (i.e. MapPanel).  If there are
        // more than one of a given panel type, the subsequent panel IDs will have an index suffix to make
        // them unique (i.e. MapPanel1).
        const newPanelTypeCounts = {};
        const newPanels = Children.toArray(newChildren).map((panel) => {
            const panelType = panel.type.name;
            const hasCount = newPanelTypeCounts[panelType] !== undefined;
            newPanelTypeCounts[panelType] = hasCount ? newPanelTypeCounts[panelType] + 1 : 0;
            const panelId = `${pageIdPrefix}-${panelType}${
                newPanelTypeCounts[panelType] > 0 ? newPanelTypeCounts[panelType] : ''
            }`;
            return wrapPanel(panel, panelId);
        });
        setPanels(newPanels);
        setPanelTypeCounts(newPanelTypeCounts);
    };

    useEffect(() => {
        updatePanels.current(children);
    }, [children]);

    return (
        <>
            <Alerts />
            <div ref={scrollContainerRef} className={clsx('scroll-container', styles.panelScrollContainer)}>
                <Layout containerSize={scrollContainerSize} panelProvider={createPanel.current} {...layoutProps}>
                    {panels}
                </Layout>
            </div>
        </>
    );
}

PanelContainer.propTypes = {
    children: node,
    panelProvider: func,
};

PanelContainer.defaultProps = {
    children: undefined,
    panelProvider: noop,
};
